import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { Modal, Field, inputCls, StatusBadge, fmtMoney } from "@/components/admin/FormBits";
import PurchaseForm from "./PurchaseForm";

const STATUSES = ["", "ordered", "received", "cancelled"];

export default function AdminPurchases() {
    const [items, setItems] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [summary, setSummary] = useState(null);
    const [editing, setEditing] = useState(null);
    const [status, setStatus] = useState("");

    const load = useCallback(() => {
        api.get("/admin/purchases", { params: status ? { status } : {} }).then((r) => setItems(r.data)).catch(() => {});
        api.get("/admin/purchases/summary").then((r) => setSummary(r.data)).catch(() => {});
    }, [status]);
    useEffect(load, [load]);
    useEffect(() => { api.get("/admin/suppliers").then((r) => setSuppliers(r.data)).catch(() => {}); }, []);

    async function save(payload) {
        try {
            if (payload.id) { await api.put(`/admin/purchases/${payload.id}`, payload); toast.success("Purchase updated"); }
            else { await api.post("/admin/purchases", payload); toast.success("Purchase recorded"); }
            setEditing(null); load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    }

    async function remove(id) {
        if (!confirm("Delete this purchase record?")) return;
        await api.delete(`/admin/purchases/${id}`); toast.success("Deleted"); load();
    }

    return (
        <div className="p-8" data-testid="admin-purchases">
            <div className="flex items-center justify-between mb-1">
                <h1 className="font-display text-3xl font-bold">Purchases monitoring</h1>
                <button onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 font-semibold disabled:opacity-50" disabled={!suppliers.length} data-testid="add-purchase-btn">
                    <Plus className="w-4 h-4" /> Record purchase
                </button>
            </div>
            <p className="text-sm text-muted-foreground mb-6">{suppliers.length ? "Track what the company buys from suppliers, payments and deliveries." : "Add a supplier first before recording purchases."}</p>

            {summary && <SummaryCards s={summary} />}

            <div className="flex items-center gap-2 mb-4">
                {STATUSES.map((s) => (
                    <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wide border ${status === s ? "bg-ink text-white border-ink" : "bg-white border-border hover:border-ink"}`} data-testid={`purchase-filter-${s || "all"}`}>{s || "All"}</button>
                ))}
            </div>

            <div className="bg-white border border-border overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted text-left"><tr>
                        <th className="px-4 py-3">Date</th><th className="px-4 py-3">Reference</th><th className="px-4 py-3">Supplier</th><th className="px-4 py-3">Items</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3 text-right">Balance</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Payment</th><th className="px-4 py-3"></th>
                    </tr></thead>
                    <tbody>
                        {items.map((p) => (
                            <tr key={p.id} className="border-t border-border" data-testid={`purchase-row-${p.id}`}>
                                <td className="px-4 py-3 text-xs whitespace-nowrap">{p.order_date}</td>
                                <td className="px-4 py-3 font-mono text-xs">{p.reference || "—"}</td>
                                <td className="px-4 py-3 font-semibold">{p.supplier_name}</td>
                                <td className="px-4 py-3 text-xs text-muted-foreground max-w-[220px] truncate">{p.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</td>
                                <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{fmtMoney(p.total, p.currency)}</td>
                                <td className={`px-4 py-3 text-right whitespace-nowrap ${p.balance > 0 ? "text-red-600" : ""}`}>{fmtMoney(p.balance, p.currency)}</td>
                                <td className="px-4 py-3"><StatusBadge value={p.status} /></td>
                                <td className="px-4 py-3"><StatusBadge value={p.payment_status} /></td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button onClick={() => setEditing(p)} className="p-2 hover:text-primary" data-testid={`edit-purchase-${p.id}`}><Edit2 className="w-4 h-4" /></button>
                                    <button onClick={() => remove(p.id)} className="p-2 hover:text-primary" data-testid={`delete-purchase-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && <tr><td colSpan="9" className="text-center py-12 text-muted-foreground">No purchases recorded{status ? ` with status "${status}"` : ""}.</td></tr>}
                    </tbody>
                </table>
            </div>
            {editing && <PurchaseForm initial={editing} suppliers={suppliers} onClose={() => setEditing(null)} onSave={save} />}
        </div>
    );
}

function SummaryCards({ s }) {
    const cards = [
        { label: "Total spend", value: fmtMoney(s.spend), testId: "summary-spend" },
        { label: "Paid to suppliers", value: fmtMoney(s.paid), testId: "summary-paid" },
        { label: "Outstanding balance", value: fmtMoney(s.outstanding), tone: s.outstanding > 0 ? "text-red-600" : "", testId: "summary-outstanding" },
        { label: "Awaiting delivery", value: s.pending, testId: "summary-pending" },
    ];
    const max = Math.max(1, ...s.monthly.map((m) => m.total));
    return (
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
                {cards.map((c) => (
                    <div key={c.label} className="bg-white border border-border p-5" data-testid={c.testId}>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground">{c.label}</div>
                        <div className={`font-display text-2xl font-bold mt-1 ${c.tone || ""}`}>{c.value}</div>
                    </div>
                ))}
            </div>
            <div className="bg-white border border-border p-5">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Monthly spend</div>
                {s.monthly.length === 0 && <div className="text-sm text-muted-foreground">No data yet</div>}
                <div className="space-y-2">
                    {s.monthly.map((m) => (
                        <div key={m._id} className="flex items-center gap-2 text-xs">
                            <span className="w-14 font-mono">{m._id}</span>
                            <div className="flex-1 bg-muted h-3"><div className="bg-primary h-3" style={{ width: `${(m.total / max) * 100}%` }} /></div>
                            <span className="w-24 text-right">{fmtMoney(m.total)}</span>
                        </div>
                    ))}
                </div>
                {s.by_supplier.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-border">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Top suppliers</div>
                        {s.by_supplier.map((x) => <div key={x._id} className="flex justify-between text-xs py-0.5"><span>{x._id}</span><span className="font-semibold">{fmtMoney(x.total)}</span></div>)}
                    </div>
                )}
            </div>
        </div>
    );
}
